import { CNSSetLayoutStyles } from "./CNSSetLayoutStyles";
import { TextAlignTypes } from "./TextAlignTypes";

export interface BlockBaseModel {
  id: string;
  categoryId: string;
  name: string;
  tags: string[];
  type: string;
  originUrl: string;
  previewUrl: string;
  iconUrl: string;
  category: string;
  description: string;
  priority: number;
  createTime: string;
  updateTime: string;
  license: string;
  imageDirection: ImageDirection;
}

export interface BackgroundThemeModel {
}

export interface TextThemeModel extends BackgroundThemeModel {
  textAlignType: TextAlignTypes;
}

export interface ColorNamesColorPresetModel {
  presetName: string;
  textColorPresets: string[];
}

export interface ColorNamesBlockModel {
  presetModels: ColorNamesColorPresetModel[];
  colorNamesThemeLayout: CNSSetLayoutStyles;
}

export type ColorNamesColorPresetModels = ColorNamesColorPresetModel[];

export const ColorNamesTextBlockNum = 31;
export const GraphicNamesTextBlockNum = 6;
export const GraphicNames12CutTextBlockNum = 12;

export type SwitchableBlockModel =
  | ColorNamesBlockModel
  | TextThemeModel
  | BackgroundThemeModel;

export enum ImageDirection {
  horizontal = "horizontal",
  vertical = "vertical",
}

export interface BlockJsonModel {
  id: string;
  packageId? : string;
  categoryId: string;
  /** block 이름 */
  name: string;
  /** block 주제명 */
  subjectName?: string;
  tags: string[];
  /** block style 타입 */
  type: string;
  /**
   * 텍스트 정렬 타입
   * 그래픽넴스, 포토넴스 등 텍스트가 들어가는 style에 적용
   */
  textAlignType: TextAlignTypes;

  layoutId: string; // 레이아웃 아이디
  /**
   * 블럭의 레이아웃 타입
   */
  layoutType: string;
  /**
   * block의 원본 이미지 url
   */
  originUrl: string;
  /**
   * block의 미리보기 이미지 url
   */
  previewUrl: string;
  /**
   * block의 아이콘 이미지 url
   */
  iconUrl: string;
  /**
   * block의 커팅용 이미지 url
   */
  cutUrl?: string;

  /**
   * block의 실버용 이미지 url
   */
  silverUrl?: string;

  /**
  * block의 뒷면 원본 이미지 url
  */
  backOriginUrl?: string;

  /**
  * block의 뒷면 실버용 이미지 url
  */
  backSilverUrl?: string;

  /**
   * 카테고리 명
   */
  category: string;
  /**
   * 라이센스 명
   */
  license: string;
  licenseId?: string;
  /**
   * 설명
   */
  description: string;
  /**
   * 정렬 우선순위
   */
  priority: number;
  createTime: string;
  updateTime: string;
  /**
   * block 이미지 방향
   * 가로, 세로
   */
  imageDirection: ImageDirection;
  /**
   * block 확장 기능
   * 칼라넴스인지, 배경용인지 등에 따른 추가 옵션 값
   */
  switchableBlockModel: SwitchableBlockModel;
  disabled: boolean;
  deleted: boolean;
  /**
   * 테마 사용 전용 제품 지정, 따로 없으면 모든 제품에서 노출.
   */
  dedicatedProducts: string[];

  // 디자인 공개 날짜
  releaseDate: Date;

  // 디자인 만료 날짜;
  expireDate: Date;
   /**
   * 애니메이트 랜덤 모드 등급(카드메이커)
   */
  grade?: number; 

  mediaSize?: string;
  price?: number;
  prices?: BlockPriceModel[];
}

export interface BlockPriceModel {
  locale: string;
  price: number;
}

export interface LanguageJsonModel {
  key: string;
  name: string;
  description?: string;
}

export interface LayoutJsonModel {
  layoutId: string;
  layoutType: string;
  blockType: string;
  name: string;
  layouts: LayoutStructureModel[];
}

export interface LayoutStructureModel {
  id: string;
  type: string;
  width: number;
  height: number;
  images: LayoutStructureImageModel[];
  texts: LayoutStructureTextModel[];
}

export interface LayoutStructureImageModel {
  id: string;
  type: string;
  x: number;
  y: number;
  width: number;
  height: number;
  rotation: number;
}

export interface LayoutStructureTextModel {
  id: string;
  type: string;
  x: number;
  y: number;
  width: number;
  height: number;
  rotation: number;
  text: string;
  horizontalAlign: string;
  verticalAlign: string;
  constraint: TextConstraint;
  font: FontStyle;
  shadow: ShadowStyle;
}

export interface TextConstraint {
  limitLength: number;
  isDateFormat: boolean;
  dateFormat: string | null;
  allowedCharacterSets: string[] | null;
}

export interface FontStyle {
  family: string;
  size: number;
  color: string;
}

export interface ShadowStyle {
  color: string;
  offsetX: number;
  offsetY: number;
  blur: number;
}


export interface CategoryJsonModel {
  id?: string;
  name: string;
  description: string;
  lang: LanguageJsonModel[];
  releaseDate: Date;
  expireDate: Date;
  priority: number;
  /**
   * 카테고리 사용 전용 제품 지정, 따로 없으면 모든 제품에서 노출.
   */
  dedicatedProducts: string[];
  packageId?: string;
  price?: number;
  thumbnailUrl?: string;
  
  /**
   * 랜덤 모드 사용
   */
  probabilityTables?: ProbabilityTableModel[]
  blockCount?: number;
}

/**
 * 랜덤 모드 확률 테이블 모델
 */
export interface ProbabilityTableModel {
  tableType: string; // "single", "triple"
  probabilities: GradeProbabilityModel[];
}

export interface GradeProbabilityModel {
  grade: number;
  probability: number; // 0.2 = 20%
}

/**
 * 카테고리를 하나로 묶어서 사용하기 위한 모델
 */
export interface CategoryPackageJsonModel {
  id?: string;
  name: string;
  lang: LanguageJsonModel[];
  viewType: string;
  priority: number;
}

export interface LicenseJsonModel {
  id?: string;
  name: string;
  releaseDate: Date;
  expireDate: Date;
  thumbnailUrl?: string;
  exposureModesCount: number;
}

export interface ModeJsonModel {
  id?: string;
  name?: string;
  description?: string;
  exposureCategories?: string[];
  exposureCategoryIds?: string[];
  exposureLicenses?: string[];
  exposureLicenseIds?: string[];
}

export interface BlockTransportSystemJsonModel {
  version: string;
  blocks: Array<BlockJsonModel>;
  lastUpdateTime: string;
  blockHostUrl: string;
  categories: Array<CategoryJsonModel>;
  categoryPackages: Array<CategoryPackageJsonModel>;
  licenses: Array<LicenseJsonModel>;
  modes: Array<ModeJsonModel>;
  types: string[];
}

export interface CategoryWithBlocksModel {
  category: CategoryJsonModel;
  blocks: BlockJsonModel[];
}

export class BlockJson {
  private hostUrl: string;
  public data: BlockJsonModel;

  constructor(
    blockJsonModel: BlockJsonModel,
    hostUrl: string
  ) {
    this.data = blockJsonModel;
    this.hostUrl = hostUrl;
  }

  public getOriginUrl() {
    return `${this.hostUrl}${this.data.originUrl}`;
  }

  public getSilverUrl() {
    return `${this.hostUrl}${this.data?.silverUrl}`
  }

  public getBackOriginUrl() {
    return `${this.hostUrl}${this.data?.backOriginUrl}`
  }

  public getBackSilverUrl() {
    return `${this.hostUrl}${this.data?.backSilverUrl}`
  }

  public getPreviewUrl() {
    return `${this.hostUrl}${this.data.previewUrl}`;
  }

  public getIconUrl() {
    return `${this.hostUrl}${this.data.iconUrl}`;
  }

  public getCutUrl() {
    return `${this.hostUrl}${this.data.cutUrl}`;
  }

  public setTextAlignType(textAlignType: TextAlignTypes) {
    for (let item in TextAlignTypes) {
      if (item.toUpperCase() === textAlignType.toUpperCase()) {
        this.data.textAlignType = item as TextAlignTypes;
        return;
      }
    }
    this.data.textAlignType = TextAlignTypes.none;
  }

  public setImageDirection(imageDirection: ImageDirection) {
    for (let item in ImageDirection) {
      if (item.toUpperCase() === imageDirection.toUpperCase()) {
        this.data.imageDirection = item as ImageDirection;
        return;
      }
    }
    this.data.imageDirection = ImageDirection.horizontal;
  }

  public setColorNamesLayout(layout: CNSSetLayoutStyles) {
    let themeLayout = (this.data.switchableBlockModel as ColorNamesBlockModel);
    for (let item in CNSSetLayoutStyles) {
      if (item.toUpperCase() === layout.toUpperCase()) {
        themeLayout.colorNamesThemeLayout = item as CNSSetLayoutStyles;
        return;
      }
    }
    themeLayout.colorNamesThemeLayout = CNSSetLayoutStyles.NONE;
  }

  public getBlockType(): BlockTypes {
    for (let item in BlockTypes) {
      if (item.toUpperCase() === this.data.type.toUpperCase()) {
        return item as BlockTypes;
      }
    }
    return BlockTypes.none;
  }

  public setBlockType(type: BlockTypes) {
    for (let item in BlockTypes) {
      if (item.toUpperCase() === type.toUpperCase()) {
        this.data.type = item;
        return;
      }
    }
    this.data.type = BlockTypes.none;
  }
}

export enum BlockTypes {
  none = "none",
  theme = "theme",
  stamp = "stamp",
  integratedTheme = "integratedTheme",
  coverTheme = "coverTheme",
  graphicNames = "graphicNames",
  graphicNames12cut = "graphicNames12cut",
  badge = "badge",
  featuredPhoto = "featuredPhoto",
  colorNames = "colorNames",
  selfCuttingSticker = "selfCuttingSticker",
  selfCuttingStickerSquare = "selfCuttingStickerSquare",
  sealSticker = "sealSticker",
  phoneCaseSticker = "phoneCaseSticker",
  photoCollageSticker = "photoCollageSticker",
  plateSticker = "plateSticker",
  acrylicGoodsTheme = "acrylicGoodsTheme",
  waterGlitter = "warterGlitter",
  pencil = "pencil",
  photoCard = "photoCard",
  photoCardSilver = "photoCardSilver",
  photoCardEvolis = "photoCardEvolis",
  paperToy = "paperToy",
  collaboCalendar = "collaboCalendar",
  cardCoverStickerMini = "cardCoverStickerMini",
  cardCoverStickerNormal = "cardCoverStickerNormal",
  cardCoverStickerToss = "cardCoverStickerToss",
  cardCoverStickerTransportation = "cardCoverStickerTransportation",
  idolAteez = "idolAteez",
  cardCoverStickerDieCut = "cardCoverStickerDieCut",
  ipSpecial = "ipSpecial",
  iglooPhotoBooth = "iglooPhotoBooth",
  cheerUpSlogan = "cheerUpSlogan",
}

export enum PaperToyLayoutTypes {
  animalFigure = "animalFigure",
  humanFigure = "humanFigure",
  boxFigure = "boxFigure",
  boxFigure2 = "boxFigure2"
}

export enum PhotoCardLayoutTypes {
  bothSideFullFrame = "bothSideFullFrame", // 양면 풀 프레임
  onlyFrontSideFullFrame = "onlyFrontSideFullFrame", // 뒷면 없이 앞면 만 풀 프레임
  simpleFrameCard = "simpleFrameCard", // 심플 프레임
  studentIdCard = "studentIdCard", // 학생증
  employeeIdCard = "employeeIdCard", // 사원증
  PetIdCard = "petIdCard", // 반려동물등록증
  societyIdCard = "societyIdCard", // 신분증
  coupleIdCard = "coupleIdCard", // 커플등록증
  archFrameCard = "archFrameCard", // 아치 프레임
  pastelBoxCard = "pastelBoxCard", // 파스텔 박스
  fourCutFrameCard = "fourCutFrameCard", // 네컷프레임
  musicPlayerCard = "musicPlayerCard", // 뮤직 플레이어
  ellipseFrameCard = "ellipseFrameCard", // 타원형 프레임
  hwatuCard = "hwatuCard", // 화투 카드
  trumpCard = "trumpCard", // 트럼프 카드
  newLookMagazine = "newLookMagazine", // 잡지&포스터 뉴룩
  makeUsMagazine = "makeUsMagazine", // 잡지&포스터 메이크어스
  egotismMagazine = "egotismMagazine", // 잡지&포스터 에고이즘
  poluvMagazine = "poluvMagazine", // 잡지&포스터 매거진
  selfWorthMagazine = "selfWorthMagazine", // 잡지&포스터 셀프워스
  fullFrameSticker = "fullFrameSticker",
  paperToy = "paperToy",
  classicNew = "classicNew",
  //SME NCT 팝업스토어
  smeNctStar = "smeNctStar",
  smeNctMirror = "smeNctMirror",
  smeNctBuddy = "smeNctBuddy",
  smeNctHeart = "smeNctHeart",
  //SME 에스파 팝업스토어
  smeAespaCharacter = "smeAespaCharacter",
  smeAespaPhoto = "smeAespaPhoto",
  //SME EXO 팝업스토어
  smeExoText = "smeExoText",
  smeExoPhoto = "smeExoPhoto",
  smeTaeyeonCharacter = "smeTaeyeonCharacter",
  smeTaeyeonPhoto = "smeTaeyeonPhoto",
  smeTaeyeonMirror = "smeTaeyeonMirror",
  //SME NCT wish 팝업스토어
  smeNctDoll = "smeNctDoll",
  smeNctBakery = "smeNctBakery",
  smeNctStaff = "smeNctStaff",
  smeNctWish = "smeNctWish",
  // SME Riize 라이즈 팝업스토어
  smeRiizeA1 = "smeRiizeA1",
  smeRiizeA2 = "smeRiizeA2",
  smeRiizeB = "smeRiizeB",
  //화산귀환 팝업스토어
  returnBlade = "returnBlade",
}

export enum PhotoCardEvolisLayoutTypes {
  bothSideFullFrameEv = "bothSideFullFrameEv", // 양면 풀 프레임
  simpleFrameCardEv = "simpleFrameCardEv", // 심플 프레임
  musicPlayerCardEv = "musicPlayerCardEv", // 뮤직 플레이어
  studentIdCardEv = "studentIdCardEv", // 학생증
  employeeIdCardEv = "employeeIdCardEv", // 사원증
  petIdCardEv = "petIdCardEv", // 반려동물등록증
  societyIdCardEv = "societyIdCardEv", // 신분증
  coupleIdCardEv = "coupleIdCardEv", // 커플등록증
  rofanCardEv = "rofanCardEv", // 로판 카드
  electricCardEv = "electricCardEv", // 일렉트릭 카드
  popIdCardEv = "popIdCardEv", // 신분증 A (팝)
  headerIdCardEv = "headerIdCardEv", // 신분증 B (헤더)
}

export enum CoverThemeLayoutTypes {
  fullFrame6x8Nonecut = "fullFrame6x8Nonecut",
  multiFrame6x8Nonecut = "multiFrame6x8Nonecut",
  multiFrame6x8cut2A = "multiFrame6x8cut2A",
  multiFrame6x8cut2B = "multiFrame6x8cut2B",
  multiFrame6x8cut2C = "multiFrame6x8cut2C",
  multiFrame6x8cut3A = "multiFrame6x8cut3A",
  multiFrame6x8cut3B = "multiFrame6x8cut3B",
  multiFrame6x8cut4A = "multiFrame6x8cut4A",
  multiFrame6x8cut4B = "multiFrame6x8cut4B",
  multiFrame6x8cut4C = "multiFrame6x8cut4C",
  multiFrame6x8cut4D = "multiFrame6x8cut4D",
  multiFrame6x8cut4E = "multiFrame6x8cut4E",
  multiFrame6x8cut8A = "multiFrame6x8cut8A",
  multiFrame6x8cut9A = "multiFrame6x8cut9A",
  fullFrameNonecut = "fullFrameNonecut",
  narrowFrameNonecut = "narrowFrameNonecut",
  narrowFramecut2A = "narrowFramecut2A",
  narrowFramecut4A = "narrowFramecut4A",
  narrowFramecut4B = "narrowFramecut4B",
  miniFrame = "miniFrame",
  classicFrame = "classicFrame",
  classicNew = "classicNew"
}

export enum PhotoBoothLayoutTypes {
  photoBooth4x6cut4A = "photoBooth4x6cut4A",
}

export enum MediaSizeTypes {
  normal4x6 = "normal4x6",
  normal6x8 = "normal6x8",
  Size4X6 = "Size4X6", 
  Size6X8 = "Size6X8",
  Size4X6Split2Up = "Size4X6Split2Up", 
  Size4X6Split3Up = "Size4X6Split3Up", 
  Size6X8Panorama2Up = "Size6X8Panorama2Up",
  Size6X8Panorama3Up = "Size6X8Panorama3Up",
}

